/**
 * Groundworks take-off template column map.
 *
 * Generated from `public/GWKS Take-off Sheet V7.2.3.xlsx` — the same file downloaded
 * by "Download Excel Template" on /tender/import-takeoff and /tender/job-types.
 * Only the workbook's actual take-off entry sheets are included — non-quants
 * reference sheets (house types, rates, build-up masters, summaries) and
 * quants-named duplicate/reference variants that aren't the sheet actually
 * parsed by `groundworksTakeoffImport.ts` (e.g. "PLOT DRIVES QUANTS T-O",
 * "PLOT DRAINAGE QUANTS SW & SOUTH", "Horizontal Quants") are excluded from
 * validation. Most included sheets are "*QUANTS*"-named, but not all: Ret &
 * Screen Walls and Geowall/Tobermore Walls are both actually take off from
 * "RET & SCREEN TAKE OFF" (`groundworksTakeoffImport.ts`'s
 * `processRetainingWallsData` for the former, `processGeoToberWallFromRetScreenData`
 * for the latter, run over the same parsed sheet) — **not** the differently
 * *QUANTS*-named "RET & SCREEN WALL QUANTS " / "GEO & TOBER WALL QUANTS "
 * sheets, which are summary/output sheets carrying only a handful of named
 * cross-sheet totals (formerly mapped here in error; corrected 2026-09-08,
 * per direct feedback, to source both domains' columns from "RET & SCREEN
 * TAKE OFF" instead).
 *
 * Each key is a worksheet name (exact match, trailing spaces included where
 * the workbook has them); each value is that worksheet's row 1 cell values
 * (in order, blanks dropped), which the template treats as the sheet's
 * column identity — the workbook's own "Notes" sheet warns
 * "DO NOT ADD ANY COLUMNS TO ANY SHEET - THIS WILL AFFECT THE UPLOAD".
 * `label` is the sheet's actual human-readable column heading for that key
 * (row 1 only holds the `GWKS_*` unique key — the real header text lives
 * elsewhere in the sheet, on a row that varies per sheet and even per column
 * within one sheet). `label` is informational only — surfaced in a
 * missing-column error message to help the user identify the column — and is
 * not itself validated against the uploaded sheet's header text, since a
 * legitimate export of "PLOT C&F QUANTS" can rename the header while keeping
 * the same `GWKS_*` key. Only "PLOT C&F QUANTS" has its labels filled in so
 * far — the rest carry `key` only.
 *
 * `cellRef` is the exact cell address of that key in the reference template
 * — read directly off the template (`XLSX.utils.decode_range` + an
 * exact-string-match scan of every cell), not assumed from array position.
 * Several sheets skip columns between keys (e.g. "PLOT DRAINAGE QUANTS" has
 * no key at C1 — A1/B1 then jumps to D1; "SCAFFOLD QUANTS" skips even more:
 * A1, B1, D1, F1, H1, then K1 onward; "RET & SCREEN TAKE OFF" skips C1/D1 —
 * A1, B1, then E1 onward), so a naive "index 0 = column A, index 1 = column
 * B, ..." derivation is wrong for every sheet except "PLOT C&F QUANTS" and
 * "PLOT FOUNDS QUANTS", which happen to have no gaps. Also informational
 * only, same as `label`.
 *
 * Regenerate by re-reading the template whenever it changes (`key`/`cellRef`
 * from an exact-match cell scan as above; `label`, where applicable, from
 * the sheet's own header row).
 */
export interface TakeoffColumnDefinition {
  /** The `GWKS_*` unique key, as it appears in the sheet. */
  key: string;
  /** Human-readable column heading, once captured for this column. */
  label?: string;
  /** Exact cell address (e.g. `"A1"`, `"E2"`) where this key lives in the reference template. */
  cellRef: string;
}

export const GROUNDWORKS_TAKEOFF_COLUMN_MAP: Record<string, TakeoffColumnDefinition[]> = {
  "PLOT C&F QUANTS": [
    { key: "GWKS_PCF_001", cellRef: "A1" },
    { key: "GWKS_PCF_002", cellRef: "B1" },
    { key: "GWKS_PCF_003", label: "Foundation Type", cellRef: "C1" },
    { key: "GWKS_PCF_004", cellRef: "D1" },
    { key: "GWKS_PCF_005", cellRef: "E1" },
    { key: "GWKS_PCF_006", cellRef: "F1" },
    { key: "GWKS_PCF_007", cellRef: "G1" },
    { key: "GWKS_PCF_008", cellRef: "H1" },
    { key: "GWKS_PCF_009", cellRef: "I1" },
    { key: "GWKS_PCF_010", cellRef: "J1" },
    { key: "GWKS_PCF_011", cellRef: "K1" },
    { key: "GWKS_PCF_012", cellRef: "L1" },
    { key: "GWKS_PCF_013", cellRef: "M1" },
    { key: "GWKS_PCF_014", cellRef: "N1" },
    { key: "GWKS_PCF_015", cellRef: "O1" },
    { key: "GWKS_PCF_016", cellRef: "P1" },
  ],
  "PLOT FOUNDS QUANTS": [
    { key: "GWKS_PF_001", cellRef: "A1" },
    { key: "GWKS_PF_002", cellRef: "B1" },
    { key: "GWKS_PF_003", cellRef: "C1" },
    { key: "GWKS_PF_004", cellRef: "D1" },
    { key: "GWKS_PF_005", cellRef: "E1" },
    { key: "GWKS_PF_006", cellRef: "F1" },
    { key: "GWKS_PF_007", cellRef: "G1" },
    { key: "GWKS_PF_008", cellRef: "H1" },
    { key: "GWKS_PF_009", cellRef: "I1" },
    { key: "GWKS_PF_010", cellRef: "J1" },
    { key: "GWKS_PF_011", cellRef: "K1" },
    { key: "GWKS_PF_012", cellRef: "L1" },
    { key: "GWKS_PF_013", cellRef: "M1" },
    { key: "GWKS_PF_014", cellRef: "N1" },
    { key: "GWKS_PF_015", cellRef: "O1" },
    { key: "GWKS_PF_016", cellRef: "P1" },
    { key: "GWKS_PF_017", cellRef: "Q1" },
    { key: "GWKS_PF_018", cellRef: "R1" },
    { key: "GWKS_PF_019", cellRef: "S1" },
    { key: "GWKS_PF_020", cellRef: "T1" },
    { key: "GWKS_PF_021", cellRef: "U1" },
    { key: "GWKS_PF_022", cellRef: "V1" },
    { key: "GWKS_PF_023", cellRef: "W1" },
    { key: "GWKS_PF_024", cellRef: "X1" },
    { key: "GWKS_PF_025", cellRef: "Y1" },
    { key: "GWKS_PF_026", cellRef: "Z1" },
    { key: "GWKS_PF_027", cellRef: "AA1" },
    { key: "GWKS_PF_028", cellRef: "AB1" },
    { key: "GWKS_PF_029", cellRef: "AC1" },
    { key: "GWKS_PF_030", cellRef: "AD1" },
    { key: "GWKS_PF_031", cellRef: "AE1" },
    { key: "GWKS_PF_032", cellRef: "AF1" },
    { key: "GWKS_PF_033", cellRef: "AG1" },
    { key: "GWKS_PF_034", cellRef: "AH1" },
    { key: "GWKS_PF_035", cellRef: "AI1" },
    { key: "GWKS_PF_036", cellRef: "AJ1" },
    { key: "GWKS_PF_037", cellRef: "AK1" },
    { key: "GWKS_PF_038", cellRef: "AL1" },
    { key: "GWKS_PF_039", cellRef: "AM1" },
    { key: "GWKS_PF_040", cellRef: "AN1" },
    { key: "GWKS_PF_041", cellRef: "AO1" },
    { key: "GWKS_PF_042", cellRef: "AP1" },
    { key: "GWKS_PF_043", cellRef: "AQ1" },
    { key: "GWKS_PF_044", cellRef: "AR1" },
    { key: "GWKS_PF_045", cellRef: "AS1" },
    { key: "GWKS_PF_046", cellRef: "AT1" },
    { key: "GWKS_PF_047", cellRef: "AU1" },
    { key: "GWKS_PF_048", cellRef: "AV1" },
    { key: "GWKS_PF_049", cellRef: "AW1" },
    { key: "GWKS_PF_050", cellRef: "AX1" },
    { key: "GWKS_PF_051", cellRef: "AY1" },
    { key: "GWKS_PF_052", cellRef: "AZ1" },
    { key: "GWKS_PF_053", cellRef: "BA1" },
    { key: "GWKS_PF_054", cellRef: "BB1" },
    { key: "GWKS_PF_055", cellRef: "BC1" },
    { key: "GWKS_PF_056", cellRef: "BD1" },
    { key: "GWKS_PF_057", cellRef: "BE1" },
    { key: "GWKS_PF_058", cellRef: "BF1" },
    { key: "GWKS_PF_059", cellRef: "BG1" },
    { key: "GWKS_PF_060", cellRef: "BH1" },
    { key: "GWKS_PF_061", cellRef: "BI1" },
    { key: "GWKS_PF_062", cellRef: "BJ1" },
    { key: "GWKS_PF_063", cellRef: "BK1" },
    { key: "GWKS_PF_064", cellRef: "BL1" },
    { key: "GWKS_PF_065", cellRef: "BN1" },
    { key: "GWKS_PF_066", cellRef: "BO1" },
    { key: "GWKS_PF_067", cellRef: "BP1" },
    { key: "GWKS_PF_068", cellRef: "BQ1" },
    { key: "GWKS_PF_069", cellRef: "BR1" },
    { key: "GWKS_PF_070", cellRef: "BS1" },
    { key: "GWKS_PF_071", cellRef: "BT1" },
    { key: "GWKS_PF_072", cellRef: "BU1" },
    { key: "GWKS_PF_073", cellRef: "BV1" },
    { key: "GWKS_PF_074", cellRef: "BW1" },
    { key: "GWKS_PF_075", cellRef: "BX1" },
    { key: "GWKS_PF_076", cellRef: "BY1" },
    { key: "GWKS_PF_077", cellRef: "BZ1" },
    { key: "GWKS_PF_078", cellRef: "CA1" },
    { key: "GWKS_PF_079", cellRef: "CB1" },
  ],
  "PLOT DRAINAGE QUANTS": [
    { key: "GWKS_PD_001", cellRef: "A1" },
    { key: "GWKS_PD_002", cellRef: "B1" },
    { key: "GWKS_PD_003", cellRef: "D1" },
    { key: "GWKS_PD_004", cellRef: "E1" },
    { key: "GWKS_PD_005", cellRef: "F1" },
    { key: "GWKS_PD_006", cellRef: "G1" },
    { key: "GWKS_PD_007", cellRef: "H1" },
    { key: "GWKS_PD_008", cellRef: "I1" },
    { key: "GWKS_PD_009", cellRef: "J1" },
    { key: "GWKS_PD_010", cellRef: "K1" },
    { key: "GWKS_PD_011", cellRef: "L1" },
    { key: "GWKS_PD_012", cellRef: "M1" },
    { key: "GWKS_PD_013", cellRef: "N1" },
    { key: "GWKS_PD_014", cellRef: "O1" },
    { key: "GWKS_PD_015", cellRef: "P1" },
    { key: "GWKS_PD_016", cellRef: "Q1" },
    { key: "GWKS_PD_017", cellRef: "R1" },
    { key: "GWKS_PD_018", cellRef: "S1" },
    { key: "GWKS_PD_019", cellRef: "T1" },
    { key: "GWKS_PD_020", cellRef: "U1" },
    { key: "GWKS_PD_021", cellRef: "V1" },
    { key: "GWKS_PD_022", cellRef: "W1" },
    { key: "GWKS_PD_023", cellRef: "X1" },
    { key: "GWKS_PD_024", cellRef: "Y1" },
    { key: "GWKS_PD_025", cellRef: "Z1" },
    { key: "GWKS_PD_026", cellRef: "AA1" },
    { key: "GWKS_PD_027", cellRef: "AB1" },
    { key: "GWKS_PD_028", cellRef: "AC1" },
    { key: "GWKS_PD_029", cellRef: "AD1" },
    { key: "GWKS_PD_030", cellRef: "AE1" },
    { key: "GWKS_PD_031", cellRef: "AF1" },
    { key: "GWKS_PD_032", cellRef: "AG1" },
    { key: "GWKS_PD_033", cellRef: "AH1" },
    { key: "GWKS_PD_034", cellRef: "AI1" },
    { key: "GWKS_PD_035", cellRef: "AJ1" },
    { key: "GWKS_PD_036", cellRef: "AK1" },
    { key: "GWKS_PD_037", cellRef: "AL1" },
    { key: "GWKS_PD_038", cellRef: "AM1" },
    { key: "GWKS_PD_039", cellRef: "AR1" },
    { key: "GWKS_PD_040", cellRef: "AS1" },
    { key: "GWKS_PD_041", cellRef: "AT1" },
    { key: "GWKS_PD_042", cellRef: "AU1" },
    { key: "GWKS_PD_043", cellRef: "AV1" },
    { key: "GWKS_PD_044", cellRef: "AW1" },
    { key: "GWKS_PD_045", cellRef: "AX1" },
    { key: "GWKS_PD_046", cellRef: "AY1" },
    { key: "GWKS_PD_047", cellRef: "AZ1" },
    { key: "GWKS_PD_048", cellRef: "BA1" },
    { key: "GWKS_PD_049", cellRef: "BB1" },
    { key: "GWKS_PD_050", cellRef: "BC1" },
    { key: "GWKS_PD_051", cellRef: "BD1" },
    { key: "GWKS_PD_052", cellRef: "BE1" },
    { key: "GWKS_PD_053", cellRef: "BF1" },
    { key: "GWKS_PD_054", cellRef: "BG1" },
    { key: "GWKS_PD_055", cellRef: "BH1" },
    { key: "GWKS_PD_056", cellRef: "BI1" },
    { key: "GWKS_PD_057", cellRef: "BJ1" },
    { key: "GWKS_PD_058", cellRef: "BK1" },
    { key: "GWKS_PD_059", cellRef: "BL1" },
    { key: "GWKS_PD_060", cellRef: "BM1" },
    { key: "GWKS_PD_061", cellRef: "BN1" },
    { key: "GWKS_PD_062", cellRef: "BO1" },
    { key: "GWKS_PD_063", cellRef: "BP1" },
    { key: "GWKS_PD_064", cellRef: "BQ1" },
    { key: "GWKS_PD_065", cellRef: "BR1" },
    { key: "GWKS_PD_066", cellRef: "BS1" },
    { key: "GWKS_PD_067", cellRef: "BT1" },
    { key: "GWKS_PD_068", cellRef: "BU1" },
    { key: "GWKS_PD_069", cellRef: "BV1" },
    { key: "GWKS_PD_070", cellRef: "BW1" },
    { key: "GWKS_PD_071", cellRef: "BX1" },
    { key: "GWKS_PD_072", cellRef: "BY1" },
    { key: "GWKS_PD_073", cellRef: "BZ1" },
    { key: "GWKS_PD_074", cellRef: "CA1" },
  ],
  "SCAFFOLD QUANTS": [
    { key: "GWKS_S_001", cellRef: "A1" },
    { key: "GWKS_S_002", cellRef: "B1" },
    { key: "GWKS_S_003", cellRef: "D1" },
    { key: "GWKS_S_004", cellRef: "F1" },
    { key: "GWKS_S_005", cellRef: "H1" },
    { key: "GWKS_S_006", cellRef: "K1" },
    { key: "GWKS_S_007", cellRef: "L1" },
    { key: "GWKS_S_008", cellRef: "M1" },
    { key: "GWKS_S_009", cellRef: "N1" },
    { key: "GWKS_S_010", cellRef: "O1" },
    { key: "GWKS_S_011", cellRef: "P1" },
    { key: "GWKS_S_012", cellRef: "Q1" },
    { key: "GWKS_S_013", cellRef: "R1" },
    { key: "GWKS_S_014", cellRef: "S1" },
    { key: "GWKS_S_015", cellRef: "T1" },
    { key: "GWKS_S_016", cellRef: "U1" },
    { key: "GWKS_S_017", cellRef: "V1" },
  ],
  "PLOT DRIVES QUANTS": [
    { key: "GWKS_PDRV_001", cellRef: "A1" },
    { key: "GWKS_PDRV_002", cellRef: "B1" },
    { key: "GWKS_PDRV_003", cellRef: "E1" },
    { key: "GWKS_PDRV_004", cellRef: "F1" },
    { key: "GWKS_PDRV_005", cellRef: "G1" },
    { key: "GWKS_PDRV_006", cellRef: "H1" },
    { key: "GWKS_PDRV_007", cellRef: "I1" },
    { key: "GWKS_PDRV_008", cellRef: "J1" },
    { key: "GWKS_PDRV_009", cellRef: "K1" },
    { key: "GWKS_PDRV_010", cellRef: "L1" },
    { key: "GWKS_PDRV_011", cellRef: "M1" },
    { key: "GWKS_PDRV_012", cellRef: "N1" },
    { key: "GWKS_PDRV_013", cellRef: "O1" },
    { key: "GWKS_PDRV_014", cellRef: "P1" },
    { key: "GWKS_PDRV_015", cellRef: "Q1" },
    { key: "GWKS_PDRV_016", cellRef: "R1" },
    { key: "GWKS_PDRV_017", cellRef: "S1" },
    { key: "GWKS_PDRV_018", cellRef: "T1" },
    { key: "GWKS_PDRV_019", cellRef: "U1" },
    { key: "GWKS_PDRV_020", cellRef: "V1" },
    { key: "GWKS_PDRV_021", cellRef: "W1" },
    { key: "GWKS_PDRV_022", cellRef: "X1" },
    { key: "GWKS_PDRV_023", cellRef: "Y1" },
    { key: "GWKS_PDRV_024", cellRef: "Z1" },
    { key: "GWKS_PDRV_025", cellRef: "AA1" },
    { key: "GWKS_PDRV_026", cellRef: "AB1" },
    { key: "GWKS_PDRV_027", cellRef: "AC1" },
    { key: "GWKS_PDRV_028", cellRef: "AD1" },
    { key: "GWKS_PDRV_029", cellRef: "AE1" },
    { key: "GWKS_PDRV_030", cellRef: "AF1" },
    { key: "GWKS_PDRV_031", cellRef: "AG1" },
    { key: "GWKS_PDRV_032", cellRef: "AH1" },
    { key: "GWKS_PDRV_033", cellRef: "AI1" },
    { key: "GWKS_PDRV_034", cellRef: "AJ1" },
    { key: "GWKS_PDRV_035", cellRef: "AK1" },
    { key: "GWKS_PDRV_036", cellRef: "AL1" },
    { key: "GWKS_PDRV_037", cellRef: "AM1" },
    { key: "GWKS_PDRV_038", cellRef: "AN1" },
    { key: "GWKS_PDRV_039", cellRef: "AO1" },
    { key: "GWKS_PDRV_040", cellRef: "AP1" },
    { key: "GWKS_PDRV_041", cellRef: "AQ1" },
    { key: "GWKS_PDRV_042", cellRef: "AR1" },
    { key: "GWKS_PDRV_043", cellRef: "AS1" },
    { key: "GWKS_PDRV_044", cellRef: "AT1" },
    { key: "GWKS_PDRV_045", cellRef: "AU1" },
    { key: "GWKS_PDRV_046", cellRef: "AV1" },
    { key: "GWKS_PDRV_047", cellRef: "AW1" },
    { key: "GWKS_PDRV_048", cellRef: "AY1" },
    { key: "GWKS_PDRV_049", cellRef: "AZ1" },
    { key: "GWKS_PDRV_050", cellRef: "BA1" },
    { key: "GWKS_PDRV_051", cellRef: "BB1" },
    { key: "GWKS_PDRV_052", cellRef: "BC1" },
    { key: "GWKS_PDRV_053", cellRef: "BD1" },
    { key: "GWKS_PDRV_054", cellRef: "BE1" },
    { key: "GWKS_PDRV_055", cellRef: "BF1" },
    { key: "GWKS_PDRV_056", cellRef: "BG1" },
    { key: "GWKS_PDRV_057", cellRef: "BH1" },
    { key: "GWKS_PDRV_058", cellRef: "BI1" },
    { key: "GWKS_PDRV_059", cellRef: "BJ1" },
    { key: "GWKS_PDRV_060", cellRef: "BK1" },
    { key: "GWKS_PDRV_061", cellRef: "BL1" },
    { key: "GWKS_PDRV_062", cellRef: "BM1" },
    { key: "GWKS_PDRV_063", cellRef: "BN1" },
    { key: "GWKS_PDRV_064", cellRef: "BO1" },
    { key: "GWKS_PDRV_065", cellRef: "BP1" },
    { key: "GWKS_PDRV_066", cellRef: "BQ1" },
    { key: "GWKS_PDRV_067", cellRef: "BR1" },
    { key: "GWKS_PDRV_068", cellRef: "BS1" },
    { key: "GWKS_PDRV_069", cellRef: "BT1" },
    { key: "GWKS_PDRV_070", cellRef: "BU1" },
    { key: "GWKS_PDRV_071", cellRef: "BV1" },
    { key: "GWKS_PDRV_072", cellRef: "BW1" },
    { key: "GWKS_PDRV_073", cellRef: "BX1" },
    { key: "GWKS_PDRV_074", cellRef: "BY1" },
    { key: "GWKS_PDRV_075", cellRef: "BZ1" },
    { key: "GWKS_PDRV_076", cellRef: "CA1" },
    { key: "GWKS_PDRV_077", cellRef: "CB1" },
  ],
  "PLOT SERVICE QUANTS ": [
    { key: "GWKS_PS_001", cellRef: "A1" },
    { key: "GWKS_PS_002", cellRef: "B1" },
    { key: "GWKS_PS_003", cellRef: "D1" },
    { key: "GWKS_PS_004", cellRef: "E1" },
    { key: "GWKS_PS_005", cellRef: "F1" },
    { key: "GWKS_PS_006", cellRef: "G1" },
    { key: "GWKS_PS_007", cellRef: "H1" },
    { key: "GWKS_PS_008", cellRef: "I1" },
    { key: "GWKS_PS_009", cellRef: "J1" },
    { key: "GWKS_PS_010", cellRef: "K1" },
    { key: "GWKS_PS_011", cellRef: "L1" },
    { key: "GWKS_PS_012", cellRef: "M1" },
    { key: "GWKS_PS_013", cellRef: "N1" },
    { key: "GWKS_PS_014", cellRef: "O1" },
    { key: "GWKS_PS_015", cellRef: "Q1" },
    { key: "GWKS_PS_016", cellRef: "R1" },
    { key: "GWKS_PS_017", cellRef: "S1" },
  ],
  "PLOT PAVING QUANTS": [
    { key: "GWKS_PP_001", cellRef: "A1" },
    { key: "GWKS_PP_002", cellRef: "B1" },
    { key: "GWKS_PP_003", cellRef: "D1" },
    { key: "GWKS_PP_004", cellRef: "E1" },
    { key: "GWKS_PP_005", cellRef: "F1" },
    { key: "GWKS_PP_006", cellRef: "G1" },
    { key: "GWKS_PP_007", cellRef: "H1" },
    { key: "GWKS_PP_008", cellRef: "J1" },
    { key: "GWKS_PP_009", cellRef: "K1" },
    { key: "GWKS_PP_010", cellRef: "L1" },
    { key: "GWKS_PP_011", cellRef: "M1" },
    { key: "GWKS_PP_012", cellRef: "N1" },
    { key: "GWKS_PP_013", cellRef: "O1" },
    { key: "GWKS_PP_014", cellRef: "P1" },
    { key: "GWKS_PP_015", cellRef: "Q1" },
    { key: "GWKS_PP_016", cellRef: "R1" },
    { key: "GWKS_PP_017", cellRef: "S1" },
    { key: "GWKS_PP_018", cellRef: "T1" },
    { key: "GWKS_PP_019", cellRef: "U1" },
    { key: "GWKS_PP_020", cellRef: "V1" },
    { key: "GWKS_PP_021", cellRef: "W1" },
    { key: "GWKS_PP_022", cellRef: "X1" },
    { key: "GWKS_PP_023", cellRef: "Y1" },
    { key: "GWKS_PP_024", cellRef: "Z1" },
    { key: "GWKS_PP_025", cellRef: "AA1" },
    { key: "GWKS_PP_026", cellRef: "AB1" },
    { key: "GWKS_PP_027", cellRef: "AC1" },
    { key: "GWKS_PP_028", cellRef: "AD1" },
    { key: "GWKS_PP_029", cellRef: "AE1" },
    { key: "GWKS_PP_030", cellRef: "AF1" },
  ],
  "GARDEN QUANTS": [
    { key: "GWKS_G_001", cellRef: "A1" },
    { key: "GWKS_G_002", cellRef: "B1" },
    { key: "GWKS_G_003", cellRef: "D1" },
    { key: "GWKS_G_004", cellRef: "G1" },
    { key: "GWKS_G_005", cellRef: "H1" },
    { key: "GWKS_G_006", cellRef: "I1" },
    { key: "GWKS_G_007", cellRef: "J1" },
    { key: "GWKS_G_008", cellRef: "K1" },
    { key: "GWKS_G_009", cellRef: "L1" },
    { key: "GWKS_G_010", cellRef: "M1" },
    { key: "GWKS_G_011", cellRef: "N1" },
  ],
  "RET & SCREEN TAKE OFF": [
    { key: "GWKS_RG_001", cellRef: "A1" },
    { key: "GWKS_RG_002", cellRef: "B1" },
    { key: "GWKS_RG_003", cellRef: "E1" },
    { key: "GWKS_RG_004", cellRef: "F1" },
    { key: "GWKS_RG_005", cellRef: "G1" },
    { key: "GWKS_RG_006", cellRef: "H1" },
    { key: "GWKS_RG_007", cellRef: "I1" },
    { key: "GWKS_RG_008", cellRef: "J1" },
    { key: "GWKS_RG_009", cellRef: "K1" },
    { key: "GWKS_RG_010", cellRef: "L1" },
    { key: "GWKS_RG_011", cellRef: "M1" },
    { key: "GWKS_RG_012", cellRef: "N1" },
    { key: "GWKS_RG_013", cellRef: "O1" },
    { key: "GWKS_RG_014", cellRef: "P1" },
    { key: "GWKS_RG_015", cellRef: "Q1" },
    { key: "GWKS_RG_016", cellRef: "R1" },
    { key: "GWKS_RG_017", cellRef: "S1" },
    { key: "GWKS_RG_018", cellRef: "T1" },
    { key: "GWKS_RG_019", cellRef: "U1" },
    { key: "GWKS_RG_020", cellRef: "V1" },
    { key: "GWKS_RG_021", cellRef: "W1" },
    { key: "GWKS_RG_022", cellRef: "X1" },
    { key: "GWKS_RG_023", cellRef: "Y1" },
    { key: "GWKS_RG_024", cellRef: "Z1" },
    { key: "GWKS_RG_025", cellRef: "AA1" },
    { key: "GWKS_RG_026", cellRef: "AB1" },
    { key: "GWKS_RG_027", cellRef: "AC1" },
    { key: "GWKS_RG_028", cellRef: "AD1" },
    { key: "GWKS_RG_029", cellRef: "AE1" },
    { key: "GWKS_RG_030", cellRef: "AF1" },
    { key: "GWKS_RG_031", cellRef: "AG1" },
    { key: "GWKS_RG_032", cellRef: "AH1" },
    { key: "GWKS_RG_033", cellRef: "AI1" },
    { key: "GWKS_RG_034", cellRef: "AJ1" },
    { key: "GWKS_RG_035", cellRef: "AK1" },
    { key: "GWKS_RG_036", cellRef: "AL1" },
    { key: "GWKS_RG_037", cellRef: "AM1" },
    { key: "GWKS_RG_038", cellRef: "AN1" },
    { key: "GWKS_RG_039", cellRef: "AO1" },
    { key: "GWKS_RG_040", cellRef: "AP1" },
    { key: "GWKS_RG_041", cellRef: "AQ1" },
    { key: "GWKS_RG_042", cellRef: "AR1" },
    { key: "GWKS_RG_043", cellRef: "AS1" },
    { key: "GWKS_RG_044", cellRef: "AT1" },
    { key: "GWKS_RG_045", cellRef: "AU1" },
    { key: "GWKS_RG_046", cellRef: "AV1" },
    { key: "GWKS_RG_047", cellRef: "AW1" },
    { key: "GWKS_RG_048", cellRef: "AX1" },
    { key: "GWKS_RG_049", cellRef: "AY1" },
    { key: "GWKS_RG_050", cellRef: "AZ1" },
    { key: "GWKS_RG_051", cellRef: "BA1" },
    { key: "GWKS_RG_052", cellRef: "BB1" },
  ],
};
